// ============================================================================
// Invoice OCR parser
// Stage 1: Detect the invoice's OWN table header from OCR word positions and
// build a "temporary table" preserving the invoice layout (columns can be
// shuffled / use short forms).
// Stage 2: Map detected columns onto the purchase table fields.
// Robust fallback: if no header is found, parse rows by content heuristics
// (qty*rate≈amount) so the feature always returns usable items.
// ============================================================================

// ---- Normalization helpers ----
function norm(s) {
  return String(s || '')
    .toLowerCase()
    .replace(/[()]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function stripMoney(s) {
  return String(s || '')
    .replace(/[₹,\u20B9?]/g, '')
    .replace(/rs\.?/gi, '')
    .replace(/\/-*/g, '')
    .replace(/[-–]$/, '')
    .replace(/%/g, '')
    .trim();
}

function isNumeric(s) {
  if (s === null || s === undefined || s === '') return false;
  const cleaned = stripMoney(s);
  if (!cleaned) return false;
  return /^[-+]?\d+(\.\d+)?$/.test(cleaned) || /^[-+]?\d*\.\d+$/.test(cleaned);
}

function parseNumber(s) {
  if (s === null || s === undefined || s === '') return null;
  const cleaned = stripMoney(s);
  if (!cleaned || !isNumeric(cleaned)) return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

function stripNoise(s) {
  return String(s || '')
    .replace(/[|_\-=]{2,}/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function extractNumberFromColumn(text) {
  const t = String(text || '');
  const m = t.match(/[-+]?\d+(?:\.\d+)?/);
  return m ? parseNumber(m[0]) : null;
}

function splitQtyUnit(text) {
  const t = String(text || '').trim();
  const m = t.match(/(\d+(?:\.\d+)?)\s*([A-Za-z%]+)?/);
  if (!m) return { qty: null, unit: '' };
  return { qty: parseNumber(m[1]), unit: m[2] || '' };
}

// ---- Header keyword dictionary (incl. short forms / variants) ----
const HEADER_DICT = {
  srNo: ['sr', 'srno', 'sno', 'sl', 'sln', 'slno', 'no', 'num', '#', 's.no', 'sr.no', 'sl.no', 'srno', 'slno', 'srn'],
  item: [
    'description', 'desc', 'descr', 'particulars', 'particular', 'part', 'item', 'items', 'itemname', 'item name',
    'itemdescription', 'item description', 'goods', 'product', 'products', 'productname', 'product name',
    'material', 'service', 'services', 'details', 'nomenclature', 'name', 'article', 'stock', 'stockitem',
    'nameofgoods', 'name of goods', 'descriptionofgoods', 'description of goods', 'particularofgoods',
    'particular of goods', 'supply', 'gooddescription', 'goods description', 'title', 'itemdetails', 'item details',
  ],
  hsn: [
    'hsn', 'hsncode', 'hsn code', 'hsn/sac', 'hsn/sac code', 'hsnsac', 'hsnsaccode', 'sac', 'saccode', 'sac code',
    'hs', 'hscode', 'hs code', 'h.s.n', 'hsnno', 'hsn no', 'hsncodes', 'gsthsn', 'gst hsn',
  ],
  qty: [
    'qty', 'qnty', 'quantity', 'quant', 'qnt', 'qty(pcs)', 'qtypcs', 'qtynos', 'qty nos', 'noofpcs', 'no of pcs',
    'noofunits', 'no of units', 'qty(gross)', 'qtygross', 'quantitynos', 'quantity nos', 'qtyunit', 'qty unit',
    'qty/free', 'qtyfree', 'qty free', 'qtyuom', 'qty uom', 'pcs', 'nos', 'units', 'packs', 'pkts', 'qtypack',
    'qty(pack)', 'qtybox', 'qty(box)', 'qty(ltr)', 'qty(kg)', 'qtyg', 'qty(g)', 'quantityg', 'no', 'n', 'q', 'totalqty',
  ],
  unit: ['unit', 'uom', 'units', 'per', 'un', 'packsize', 'pack size', 'pack', 'uomcode', 'uom code'],
  rate: [
    'rate', 'rt', 'price', 'unitprice', 'unit price', 'rate/unit', 'rateunit', 'rateperunit', 'rate per unit',
    'rateper', 'rate per', 'per unit', 'amount/unit', 'amountunit', 'unitrate', 'unit rate', 'rs', 'rs.', 'rt.',
    'price/unit', 'priceunit', 'rateperuom', 'rate per uom', 'rateuom', 'rate uom', 'value/unit', 'valueunit',
    'unitcost', 'unit cost', 'rate(rs)', 'rate rs', 'pricers', 'price rs', 'rateperkg', 'rateperpcs', 'rateperpack',
  ],
  mrp: [
    'mrp', 'mrp/unit', 'mrp unit', 'mrp/unit', 'maxretail', 'max retail', 'max retail price', 'm.r.p', 'mrpprice',
    'mrp price', 'mrp(rs)', 'mrp rs', 'listprice', 'list price', 'lsrp', 'lsrp/unit',
  ],
  discount: [
    'disc', 'discount', 'dis', 'discount%', 'disc %', 'disc%', 'disc.', 'discamt', 'discountamt', 'discount amt',
    'discountvalue', 'discount value', 'dis%', 'd', 'discount(rs)', 'discount rs',
  ],
  gst: [
    'gst', 'gst%', 'gst %', 'gstpercent', 'gst percent', 'gstrate', 'gst rate', 'gstslab', 'gst slab', 'cgst',
    'cgst%', 'cgst %', 'cgstrate', 'cgst rate', 'sgst', 'sgst%', 'sgst %', 'sgstrate', 'sgst rate', 'igst',
    'igst%', 'igst %', 'igstrate', 'igst rate', 'tax', 'tax%', 'tax %', 'taxrate', 'tax rate', 'vat', 'cess',
    'gsttax', 'gst tax', 'taxpercent', 'tax percent', 'rateofgst', 'rate of gst', 'gst(%)', 'gst(rs)',
    'taxablevalue', 'taxable value', 'taxableamt', 'taxable amt', 'cgst+sgst', 'cgstsgst', 'cgst&sgst',
  ],
  amount: [
    'amount', 'amt', 'amount(rs)', 'amount rs', 'amountrs', 'total', 'totalamount', 'total amount', 'totalamt',
    'total amt', 'value', 'totalvalue', 'total value', 'gross', 'grossamount', 'gross amount', 'grosstotal',
    'gross total', 'netamount', 'net amount', 'netamt', 'net amt', 'nettotal', 'net total', 'invoicevalue',
    'invoice value', 'billamount', 'bill amount', 'netvalue', 'net value', 'amount(incl)', 'amountincl',
    'total(rs)', 'total rs', 'value(rs)', 'value rs', 'amts', 'finalamount', 'final amount', 'netpayable',
    'net payable', 'totalvalue(rs)', 'totalvalue rs',
  ],
};

// Normalize a header word the same way classifyWord sees it
function normHeader(s) {
  return norm(s).replace(/[{}.'"\\/]/g, '');
}

function classifyWord(text) {
  const t = normHeader(text);
  if (!t) return null;

  // 1) Exact match against a normalized dictionary entry
  for (const type of Object.keys(HEADER_DICT)) {
    for (const kw of HEADER_DICT[type]) {
      if (normHeader(kw) === t) return type;
    }
  }

  // 2) Starts-with / contains for combined forms like "Qty(pcs)", "GST%"
  for (const type of Object.keys(HEADER_DICT)) {
    for (const kw of HEADER_DICT[type]) {
      const cleanKw = normHeader(kw);
      if (cleanKw && t.startsWith(cleanKw) && cleanKw.length >= 3) return type;
      if (cleanKw && t.includes(cleanKw) && cleanKw.length >= 5) return type;
    }
  }
  return null;
}

// ---- TSV parsing ----
function parseTSV(tsv) {
  if (!tsv) return [];
  const lines = String(tsv).split('\n').filter((l) => l.trim());
  if (!lines.length) return [];
  const header = lines[0].split('\t').map((h) => h.trim());
  const idx = {};
  header.forEach((h, i) => { idx[h] = i; });
  const rows = [];
  for (let i = 1; i < lines.length; i++) {
    const parts = lines[i].split('\t');
    const r = {};
    header.forEach((h, j) => { r[h] = parts[j] !== undefined ? parts[j] : ''; });
    rows.push(r);
  }
  return rows;
}

function linesFromTSV(tsv) {
  const rows = parseTSV(tsv);
  const lineMap = new Map();
  for (const r of rows) {
    if (Number(r.level) !== 5) continue;
    const key = `${r.page_num}-${r.block_num}-${r.par_num}-${r.line_num}`;
    if (!lineMap.has(key)) lineMap.set(key, []);
    lineMap.get(key).push({
      text: r.text || '',
      x0: Number(r.left) || 0,
      x1: (Number(r.left) || 0) + (Number(r.width) || 0),
      y0: Number(r.top) || 0,
    });
  }
  const lines = [];
  for (const [key, words] of lineMap) {
    words.sort((a, b) => a.x0 - b.x0);
    lines.push({ key, y0: words[0]?.y0 || 0, words });
  }
  lines.sort((a, b) => {
    const pa = a.key.split('-').map(Number);
    const pb = b.key.split('-').map(Number);
    for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
      const va = pa[i] || 0;
      const vb = pb[i] || 0;
      if (va !== vb) return va - vb;
    }
    return 0;
  });
  return lines;
}

function linesFromBlocks(data) {
  const lines = [];
  const blocks = data?.blocks || [];
  for (const block of blocks) {
    const paragraphs = block?.paragraphs || [];
    for (const para of paragraphs) {
      const plines = para?.lines || [];
      for (const line of plines) {
        const words = (line?.words || [])
          .map((w) => ({
            text: (w?.text || '').trim(),
            x0: w?.bbox?.x0 ?? 0,
            x1: w?.bbox?.x1 ?? 0,
            y0: w?.bbox?.y0 ?? 0,
          }))
          .filter((w) => w.text);
        if (words.length) {
          words.sort((a, b) => a.x0 - b.x0);
          lines.push({ y0: words[0]?.y0 || 0, words });
        }
      }
    }
  }
  return lines;
}

// ---- Detect invoice's own table header ----
// Group header words into columns using KEYWORDS (not pixel gaps):
// each classified keyword starts a new column; same-type/adjacent words merge.
// Words that complete a multi-word header phrase (e.g. "Taxable Value",
// "Item Name", "Description of Goods"). Same-type words that are qualifiers
// merge into one column; real separate columns (GST/CGST/SGST) do not.
const PHRASE_QUALIFIERS = [
  'name', 'code', 'value', 'rate', 'amount', 'total', 'of', 'the', 'per', 'unit',
  'goods', 'price', 'quantity', 'no', 'nos', 'pcs', 'details', 'desc', 'description',
];

function groupHeaderWords(words) {
  const sorted = [...words].sort((a, b) => a.x0 - b.x0);
  const groups = [];
  for (const w of sorted) {
    const c = classifyWord(w.text);
    const last = groups[groups.length - 1];
    if (c) {
      const qualifier = PHRASE_QUALIFIERS.includes(norm(w.text).replace('.', ''));
      if (last && last.type === c && qualifier && w.x0 - last.end < 120) {
        last.words.push(w);
        last.end = Math.max(last.end, w.x1);
      } else {
        groups.push({ words: [w], start: w.x0, end: w.x1, type: c });
      }
    } else {
      // Unclassified continuation word, e.g. "of", "Goods", "Code", "per"
      if (last && w.x0 - last.end < 120) {
        last.words.push(w);
        last.end = Math.max(last.end, w.x1);
      } else {
        groups.push({ words: [w], start: w.x0, end: w.x1, type: null });
      }
    }
  }
  return groups;
}

function columnTypeOfGroup(group) {
  return group.type || null;
}

function findHeaderLine(lines) {
  let bestIdx = -1;
  let best = { score: 0, types: new Set() };
  lines.forEach((line, i) => {
    const groups = groupHeaderWords(line.words);
    const types = new Set(groups.map((g) => columnTypeOfGroup(g)).filter(Boolean));
    let score = types.size;
    if (types.has('item')) score += 3;
    if (types.has('qty')) score += 2;
    if (types.has('rate')) score += 2;
    if (types.has('amount')) score += 2;
    if (types.has('hsn')) score += 1;
    if (types.has('gst')) score += 1;
    if (types.has('mrp')) score += 1;
    if (types.has('unit')) score += 1;
    if (score > best.score) {
      best = { score, types };
      bestIdx = i;
    }
  });
  if (bestIdx === -1) return -1;
  if (best.score < 2) return -1;
  const key = best.types.has('item') || best.types.has('qty') || best.types.has('rate') || best.types.has('amount');
  if (!key) return -1;
  return bestIdx;
}

function buildColumnGroups(headerLine) {
  const groups = groupHeaderWords(headerLine.words);
  return groups.map((g, i) => ({
    id: `col${i}`,
    type: columnTypeOfGroup(g),
    text: g.words.map((w) => w.text).join(' ').trim(),
    start: g.start,
    end: g.end,
  }));
}

function buildBoundaries(columns, pageWidth) {
  return columns.map((c, i) => {
    const next = columns[i + 1];
    const end = next ? (c.end + next.start) / 2 : pageWidth;
    return { id: c.id, type: c.type, start: c.start, end };
  });
}

function assignWordToColumn(x0, boundaries) {
  for (const b of boundaries) {
    if (x0 >= b.start && x0 < b.end) return b.id;
  }
  let best = null;
  let bestDist = Infinity;
  for (const b of boundaries) {
    const d = Math.abs(x0 - b.start);
    if (d < bestDist) {
      bestDist = d;
      best = b.id;
    }
  }
  return best;
}

// Qty/Free format: "0:15" means 0 free, 15 billed -> return 15.
// Also handles PDFs that split it into separate tokens: "0 : 15", "0: 15", "0 15".
function parseQtyValue(text) {
  const t = String(text || '').trim();
  const colon = t.match(/^\s*(\d+(?:\.\d+)?)\s*:\s*(\d+(?:\.\d+)?)\s*$/);
  if (colon) {
    const a = Number(colon[1]);
    const b = Number(colon[2]);
    return b > 0 ? b : a > 0 ? a : 0;
  }
  const two = t.match(/^\s*(\d+(?:\.\d+)?)\s+(\d+(?:\.\d+)?)\s*$/);
  if (two) {
    const a = Number(two[1]);
    const b = Number(two[2]);
    if (a === 0 && b > 0) return b;
    if (b === 0 && a > 0) return a;
    return Math.max(a, b);
  }
  return extractNumberFromColumn(t);
}

// Detect an unheadered trailing numeric column (usually the Amount/Total).
function detectTrailingAmountColumn(lines, headerIdx, columns) {
  const lastCol = columns[columns.length - 1];
  if (lastCol && lastCol.type === 'amount') return null; // already has an amount header

  const headerMaxX = Math.max(...lines[headerIdx].words.map((w) => w.x1), 0);

  let numericCount = 0;
  let rowCount = 0;
  for (let i = headerIdx + 1; i < lines.length; i++) {
    const line = lines[i];
    const lineText = line.words.map((w) => w.text).join(' ');
    if (isFooterLine(lineText)) continue;
    const beyond = line.words.filter(
      (w) => w.x0 >= headerMaxX && isNumeric(w.text)
    );
    if (beyond.length) {
      rowCount++;
      numericCount += beyond.length;
    }
  }

  if (numericCount >= 2 && rowCount >= 1) {
    return {
      id: 'colAmount',
      type: 'amount',
      text: 'Amount',
      start: headerMaxX,
      end: headerMaxX + 1,
    };
  }
  return null;
}

// ---- Build temporary table rows (invoice's own layout) ----
function buildTableRow(words, boundaries, columns) {
  const cell = {};
  for (const w of words) {
    const colId = assignWordToColumn(w.x0, boundaries);
    cell[colId] = (cell[colId] ? cell[colId] + ' ' : '') + w.text;
  }
  for (const c of columns) {
    if (!(c.id in cell)) cell[c.id] = '';
  }
  return cell;
}

const FOOTER_TERMS = [
  'total', 'grand total', 'round off', 'roundoff', 'sub total', 'subtotal', 'taxable amount',
  'gst amount', 'cgst', 'sgst', 'igst', 'amount chargeable', 'bank details', 'terms and conditions',
  'declaration', 'certified', 'authorised', 'signature', 'payment', 'thanks', 'thank you',
  'for ', 'received', 'delivery', 'delivery challan', 'e&oe', 'e.&o.e', 'subject to', 'gstin',
  'invoice total', 'invoice value', 'grand total(rs)', 'total(rs)', 'tax invoice', 'bill of supply',
  'credit note', 'debit note', 'original for recipient', 'duplicate for', 'triplicate for',
];

function isFooterLine(lineText) {
  const t = norm(lineText);
  if (!t) return true;
  return FOOTER_TERMS.some((ft) => t.includes(ft));
}

// ---- Default mapping: invoice column type -> purchase field ----
const TYPE_TO_FIELD = {
  item: 'itemName',
  hsn: 'hsn',
  qty: 'qty',
  unit: 'unit',
  rate: 'price',
  mrp: 'mrp',
  gst: 'gst',
  discount: 'discount',
  amount: 'totalAmt',
};

export function defaultMapping(columns) {
  const mapping = {};
  for (const c of columns) {
    mapping[c.id] = c.type ? TYPE_TO_FIELD[c.type] || null : null;
  }
  return mapping;
}

export const TARGET_FIELDS = [
  { value: '', label: '— Skip —' },
  { value: 'itemName', label: 'Item Name' },
  { value: 'mrp', label: 'MRP' },
  { value: 'hsn', label: 'HSN' },
  { value: 'qty', label: 'Qty' },
  { value: 'unit', label: 'Unit' },
  { value: 'price', label: 'Price' },
  { value: 'gst', label: 'GST %' },
  { value: 'discount', label: 'Discount' },
  { value: 'totalAmt', label: 'Amount' },
];

// ---- Main: parse OCR data into a temporary table ----
export function parseInvoiceTable(ocrData) {
  let lines = [];
  if (ocrData?.tsv) lines = linesFromTSV(ocrData.tsv);
  else if (ocrData?.blocks?.length) lines = linesFromBlocks(ocrData);

  if (!lines.length) {
    // No positional data: fall back to raw text parsing
    const textItems = parseContentItemsFromText(ocrData?.text || '');
    return { columns: [], rows: [], mapping: {}, mode: 'content', items: textItems };
  }

  const maxX = Math.max(2000, ...lines.flatMap((l) => l.words.map((w) => w.x1)));

  const headerIdx = findHeaderLine(lines);
  if (headerIdx !== -1) {
    const columns = buildColumnGroups(lines[headerIdx]);

    // Some invoices omit a header for the Amount/Total column (e.g. "... SGST CGST" with
    // amounts further right). Detect that trailing numeric column and add it as Amount.
    const trailing = detectTrailingAmountColumn(lines, headerIdx, columns);
    if (trailing) columns.push(trailing);

    const boundaries = buildBoundaries(columns, maxX);
    const rows = [];

    for (let i = headerIdx + 1; i < lines.length; i++) {
      const line = lines[i];
      const lineText = line.words.map((w) => w.text).join(' ');
      if (isFooterLine(lineText)) continue;
      const nums = line.words.filter((w) => /\d/.test(w.text));
      if (nums.length === line.words.length && nums.length < 3) continue;
      rows.push({ words: line.words, y0: line.y0 });
    }

    // Group wrapped item-name lines into logical rows
    const logicalRows = [];
    for (const r of rows) {
      const hasNumbers = r.words.some((w) => /\d/.test(w.text));
      if (!hasNumbers && logicalRows.length) {
        logicalRows[logicalRows.length - 1].lines.push(r);
      } else {
        logicalRows.push({ lines: [r] });
      }
    }

    const tableRows = [];
    for (const lr of logicalRows) {
      const allWords = lr.lines.flatMap((l) => l.words);
      const cell = buildTableRow(allWords, boundaries, columns);
      tableRows.push(cell);
    }

    if (tableRows.length) {
      const table = { columns, rows: tableRows, mapping: defaultMapping(columns), mode: 'table' };
      const goodName = (it) =>
        (it.itemName || '').split(/\s+/).some((w) => /[A-Za-z]{2,}/.test(w));

      const items = buildItemsFromTable(table, table.mapping);
      const tableGood = items.filter(goodName).length;

      // Also parse content-mode so we can pick whichever produces the most
      // complete item names (content mode reconstructs names from line text
      // and is robust even when the column boundaries misalign).
      const contentItems = parseContentItems(lines);
      const contentGood = contentItems.filter(goodName).length;

      if (
        items.length &&
        tableGood >= contentGood &&
        tableGood >= Math.max(1, items.length * 0.5)
      ) {
        return table;
      }
      if (contentItems.length) {
        return { columns: [], rows: [], mapping: {}, mode: 'content', items: contentItems };
      }
    }
  }

  // ---- Fallback: content-based (no reliable header) ----
  const items = parseContentItems(lines);
  if (items.length) {
    return { columns: [], rows: [], mapping: {}, mode: 'content', items };
  }

  // ---- Final fallback: raw text parsing ----
  const textItems = parseContentItemsFromText(ocrData?.text || '');
  return { columns: [], rows: [], mapping: {}, mode: 'content', items: textItems };
}

// ---- Content parsing (used when no reliable header is detected) ----

const COMMON_TAX_RATES = [0.5, 1, 1.5, 2.5, 3, 5, 6, 7.5, 9, 12, 14, 18, 28];

function tokenizeLine(text) {
  return String(text)
    .replace(/[|[\](){}<>"]/g, ' ')
    .split(/\s+/)
    .filter(Boolean);
}

// Parse one line/token-array into a purchase item using content heuristics.
// Understands the "Qty/Free" format ("0:15" => qty 15), HSN (6-8 digits),
// money symbols, and derives rate via qty*rate≈amount.
function parseContentLine(tokens) {
  let nameParts = [];
  const numbers = [];
  let qty = null;
  let hsn = '';

  for (const raw of tokens) {
    const t = String(raw || '').trim();
    if (!t) continue;
    // Skip pure punctuation / symbol tokens (e.g. ":", "*") — they are not
    // part of the item name and not numbers.
    if (!/[a-z0-9]/i.test(t)) continue;

    // Qty/Free pattern "0:15", "15:0", "2:1"
    const qm = t.match(/^(\d+(?:\.\d+)?):(\d+(?:\.\d+)?)$/);
    if (qm) {
      const a = Number(qm[1]);
      const b = Number(qm[2]);
      qty = b > 0 ? b : a > 0 ? a : 0;
      continue;
    }

    if (isNumeric(t)) {
      // HSN codes are integers (no decimal point). Only treat as HSN if the
      // token itself is 6-8 digits without a dot, so amounts like "1285.80"
      // are not misread as HSN.
      const cleanDigits = t.replace(/[,\s]/g, '');
      if (/^\d{6,8}$/.test(cleanDigits) && !t.includes('.')) {
        if (!hsn) hsn = cleanDigits;
        continue;
      }
      const v = parseNumber(t);
      if (v != null) numbers.push(v);
    } else {
      nameParts.push(t);
    }
  }

  let name = stripNoise(nameParts.join(' '));
  name = name.replace(/^\d+[.)]?\s*/, '');
  name = name.replace(/\s+\d+(\.\d+)?%?$/, '');

  if (!name) return null;

  let amount = null;
  let rate = null;
  let mrp = null;
  let gst = null;

  if (qty != null) {
    amount = numbers.length ? numbers[numbers.length - 1] : null;
    if (qty > 0 && amount) {
      const cand = amount / qty;
      rate = numbers.find((n) => Math.abs(n - cand) < Math.max(0.02, cand * 0.02)) ?? null;
    }
    if (rate == null && numbers.length >= 2) rate = numbers[numbers.length - 2];
  } else {
    const solved = solveQtyRateAmount(numbers);
    qty = solved.qty;
    rate = solved.rate;
    amount = solved.amount;
  }

  if (qty == null && numbers.length) {
    // fallback: treat first numeric column that is a small int as qty
    const smallInt = numbers.find((n) => Number.isInteger(n) && n > 0 && n <= 1000);
    if (smallInt != null) {
      qty = smallInt;
      if (rate == null && amount && qty > 0) rate = amount / qty;
    }
  }

  // MRP = largest remaining number (typically the max-retail price)
  {
    const rest = numbers.filter((n) => n !== rate && n !== amount && n != null);
    if (rest.length) mrp = Math.max(...rest);
  }

  // GST % = common tax rate(s) among the leftover small numbers.
  // Equal pairs (e.g. SGST 2.5 + CGST 2.5) are summed into the total GST.
  {
    const rest = numbers.filter((n) => n !== rate && n !== amount && n !== mrp && n > 0 && n <= 50);
    const common = rest.filter((n) => COMMON_TAX_RATES.includes(n));
    if (common.length >= 2) {
      const first = common[0];
      gst = common.every((n) => n === first) ? first * common.length : Math.max(...common);
    } else if (common.length === 1) {
      gst = common[0];
    } else if (rest.length) {
      gst = rest[0];
    }
  }

  return {
    itemName: name,
    hsn: String(hsn || '').trim(),
    qty: qty != null ? String(qty) : '',
    unit: '',
    price: rate != null ? rate.toFixed(2) : '',
    mrp: mrp != null ? mrp.toFixed(2) : '',
    gst: gst != null ? String(gst) : '',
    discount: '',
    totalAmt: amount != null ? amount.toFixed(2) : '',
  };
}

function dedupeItems(items) {
  const seen = new Set();
  const final = [];
  for (const it of items) {
    const key = `${it.itemName}|${it.qty}|${it.price}|${it.totalAmt}`;
    if (seen.has(key)) continue;
    seen.add(key);
    if (!it.itemName && !it.price && !it.totalAmt) continue;
    final.push(it);
  }
  return final;
}

// Lines that are buyer/seller/address info, NOT item rows. These should never
// be treated as items even in the content fallback.
const META_PATTERNS = [
  /^m\/s\b/i,
  /invoice no/i,
  /invoice\s*:/i,
  /gstin/i,
  /tin no/i,
  /fssai/i,
  /ph\.?no/i,
  /phone\s*:/i,
  /e-?mail/i,
  /\bdate\s*:/i,
  /\bharyana/i,
  /\baddress/i,
  /\broad\b/i,
  /\bgurugram/i,
  /\bgurgaon/i,
  /\bstreet/i,
  /\bhouse\s+no/i,
  /\bbank\b/i,
  /\bbranch\b/i,
  /\bac\s+no/i,
  /\bifsc/i,
  /\bdisputes/i,
  /\bprescribed/i,
  /\bcertified/i,
  /\bauthorised/i,
  /\bchecked\s+by/i,
  /\be\.?&\.?o\.?e/i,
  /\bimport\s+purchase/i,
  /\bmarg\b/i,
  /\bsub\s+total/i,
  /\bround\s*off/i,
  /\bcr\/dr\b/i,
  /\bgrand\s+total/i,
  /\bqty\s+total/i,
  /\bmrp\s+total/i,
  /\bterms?\s*&?/i,
  /\bdiscount\s/,
  /\bsgst\s+\d/i,
  /\bcgst\s+\d/i,
  /\bprice\s+off/i,
  /\bmonth\s+of/i,
  /\bgst\s+\d[\d.*+=%/]*sgst/i,
  /\bgrand\s+total/i,
];

function isMetaLine(lineText) {
  const t = String(lineText || '').toLowerCase();
  return META_PATTERNS.some((re) => re.test(t));
}

// ---- Raw text content parsing (no positions needed) ----
function parseContentItemsFromText(text) {
  if (!text) return [];
  const lines = String(text)
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);

  const items = [];
  for (const line of lines) {
    if (isFooterLine(line) || isMetaLine(line)) continue;
    const tokens = tokenizeLine(line);
    if (!tokens.some((t) => /\d/.test(t))) continue;
    const numericCount = tokens.filter((t) => /\d/.test(t)).length;
    if (numericCount < 2) continue; // item rows carry at least qty + amount
    const item = parseContentLine(tokens);
    if (item) items.push(item);
  }
  return dedupeItems(items);
}

// ---- Content fallback: parse items without header info ----
function parseContentItems(lines) {
  const items = [];
  for (const line of lines) {
    const lineText = line.words.map((w) => w.text).join(' ');
    if (isFooterLine(lineText) || isMetaLine(lineText)) continue;
    const numCount = tokenizeLine(lineText).filter((t) => /\d/.test(t)).length;
    const tokCount = tokenizeLine(lineText).length;
    if (numCount === tokCount && tokCount < 3) continue;
    if (numCount < 2) continue;

    const tokens = line.words.map((w) => w.text);
    if (!tokens.some((t) => /\d/.test(t))) continue;
    const item = parseContentLine(tokens);
    if (item) items.push(item);
  }
  return dedupeItems(items);
}

// Solve qty/rate/amount from numbers using qty*rate≈amount
function solveQtyRateAmount(nums) {
  if (!nums || !nums.length) return { qty: null, rate: null, amount: null };
  if (nums.length === 1) return { qty: 1, rate: null, amount: nums[0] };

  for (let i = 0; i < nums.length; i++) {
    for (let j = 0; j < nums.length; j++) {
      if (i === j) continue;
      const q = nums[i];
      const r = nums[j];
      if (!q || !r) continue;
      const a = q * r;
      for (let k = 0; k < nums.length; k++) {
        if (k === i || k === j) continue;
        if (Math.abs(nums[k] - a) < Math.max(0.02, a * 0.02)) {
          return { qty: q, rate: r, amount: nums[k] };
        }
      }
    }
  }

  if (nums.length === 2) {
    const lo = Math.min(nums[0], nums[1]);
    const hi = Math.max(nums[0], nums[1]);
    if (lo !== 0 && hi % lo === 0 && lo >= 2 && hi / lo <= 1000) {
      return { qty: lo, rate: hi / lo, amount: hi };
    }
    return { qty: 1, rate: lo, amount: hi };
  }

  const last3 = nums.slice(-3);
  if (last3.length === 3) return { qty: last3[0], rate: last3[1], amount: last3[2] };
  if (last3.length === 2) return { qty: 1, rate: last3[0], amount: last3[1] };
  return { qty: 1, rate: null, amount: last3[0] };
}

// ---- Stage 2: turn temporary table + mapping into purchase items ----
export function buildItemsFromTable(table, mapping) {
  const { columns, rows } = table;
  if (!columns.length || !rows.length) return [];

  const fieldCol = {};
  const fieldCols = {};
  for (const c of columns) {
    const target = mapping && mapping[c.id];
    if (target) {
      fieldCol[target] = c.id;
      fieldCols[target] = fieldCols[target] || [];
      fieldCols[target].push(c.id);
    }
  }

  const items = [];
  for (const row of rows) {
    const get = (field) => (fieldCol[field] ? String(row[fieldCol[field]] || '').trim() : '');
    const itemName = stripNoise(get('itemName'));
    const hsn = get('hsn');
    let qtyRaw = get('qty');
    let unitRaw = get('unit');
    let rateRaw = get('rate') || get('price');
    let mrpRaw = get('mrp');
    let amountRaw = get('totalAmt');
    let gstRaw = get('gst');
    let discRaw = get('discount');

    if (!itemName && !qtyRaw && !rateRaw && !amountRaw) continue;

    const qtyNum = parseQtyValue(qtyRaw);
    const rateNum = extractNumberFromColumn(rateRaw);
    const amountNum = extractNumberFromColumn(amountRaw);
    const mrpNum = extractNumberFromColumn(mrpRaw);
    const discNum = extractNumberFromColumn(discRaw);

    if (!unitRaw && qtyRaw) {
      const sq = splitQtyUnit(qtyRaw);
      if (sq.unit) unitRaw = sq.unit;
    }

    // GST: when the invoice has separate CGST + SGST (or IGST) columns,
    // combine them into the total GST %. This avoids only one half being added.
    let gstPercent = null;
    if (fieldCols.gst && fieldCols.gst.length) {
      const gstCols = fieldCols.gst.map((cid) => ({
        text: (columns.find((c) => c.id === cid)?.text || '') + ' ' + String(row[cid] || ''),
        val: extractNumberFromColumn(String(row[cid] || '')),
      }));
      const hasCGST = gstCols.some((g) => /cgst/i.test(g.text));
      const hasSGST = gstCols.some((g) => /sgst/i.test(g.text));
      const hasIGST = gstCols.some((g) => /igst/i.test(g.text));
      const hasPlainGst = gstCols.some((g) => /(^|\s)gst/i.test(g.text) && !/cgst|sgst|igst/i.test(g.text));
      const vals = gstCols.map((g) => g.val).filter((v) => v != null);

      if (hasCGST && hasSGST) {
        const cgstVal = gstCols.find((g) => /cgst/i.test(g.text))?.val || 0;
        const sgstVal = gstCols.find((g) => /sgst/i.test(g.text))?.val || 0;
        const plainVal = hasPlainGst
          ? gstCols.find((g) => /(^|\s)gst\b/i.test(g.text) && !/cgst|sgst|igst/i.test(g.text))?.val
          : null;
        // If a plain "GST" total column exists, use it (avoid double counting)
        gstPercent = plainVal != null ? plainVal : (cgstVal + sgstVal);
      } else if (hasIGST && !hasPlainGst) {
        gstPercent = gstCols.find((g) => /igst/i.test(g.text))?.val || null;
      } else if (hasPlainGst && vals.length === 1) {
        gstPercent = vals[0];
      } else if (hasPlainGst && vals.length > 1) {
        gstPercent = Math.max(...vals);
      } else if (vals.length) {
        gstPercent = vals.reduce((a, b) => a + b, 0);
      }
    } else if (gstRaw) {
      const m = String(gstRaw).match(/(\d+(?:\.\d+)?)/);
      if (m) gstPercent = parseNumber(m[1]);
    }

    let rate = rateNum;
    let amount = amountNum;
    if (rate == null && qtyNum && amount) rate = amount / qtyNum;
    if (amount == null && qtyNum && rate) amount = qtyNum * rate;

    let name = itemName.replace(/^\d+[.)]?\s*/, '');
    name = name.replace(/\s+\d+(\.\d+)?%?$/, '');

    if (!name && !rate && !amount && !qtyNum) continue;

    items.push({
      itemName: name,
      hsn: String(hsn || '').trim(),
      qty: qtyNum != null ? String(qtyNum) : '',
      unit: String(unitRaw || '').trim(),
      price: rate != null ? rate.toFixed(2) : '',
      mrp: mrpNum != null ? mrpNum.toFixed(2) : '',
      gst: gstPercent != null ? String(gstPercent) : '',
      discount: discNum != null ? String(discNum) : '',
      totalAmt: amount != null ? amount.toFixed(2) : '',
    });
  }
  return items;
}

// ---- Product matching ----
export function matchProduct(row, products) {
  if (!row?.itemName || !Array.isArray(products) || !products.length) return null;

  const needle = norm(row.itemName);
  if (!needle) return null;

  let best = null;
  let bestScore = 0;

  for (const product of products) {
    const candidates = [];
    if (product.name) candidates.push(product.name);
    if (product.variants && product.variants.length) {
      product.variants.forEach((v) => {
        if (!v.variant_name || v.variant_name === 'Default') {
          candidates.push(product.name);
        } else {
          candidates.push(`${product.name} ${v.variant_name}`);
        }
      });
    } else {
      candidates.push(product.name);
    }

    for (const candidate of candidates) {
      const cand = norm(candidate);
      if (!cand) continue;

      let score = 0;
      if (needle === cand) {
        score = 100;
      } else if (needle.includes(cand) || cand.includes(needle)) {
        score = 80;
      } else {
        const needleTokens = new Set(needle.split(' ').filter((w) => w.length > 2));
        const candTokens = cand.split(' ').filter((w) => w.length > 2);
        if (needleTokens.size && candTokens.length) {
          let overlap = 0;
          for (const ct of candTokens) {
            if (needleTokens.has(ct)) overlap++;
            else if (needle.includes(ct)) overlap += 0.5;
          }
          score = (overlap / candTokens.length) * 70;
        }
      }

      if (score > bestScore) {
        bestScore = score;
        best = {
          product,
          variant: product.variants && product.variants.length ? product.variants[0] : null,
          score,
        };
      }
    }
  }

  return bestScore >= 50 ? best : null;
}

export function fillItemFromMatch(row, match) {
  const item = {
    itemName: row.itemName || match?.product?.name || '',
    hsn: row.hsn || match?.variant?.hsn || match?.product?.hsn || '',
    qty: row.qty || '1',
    unit: row.unit || match?.variant?.unit_name || '',
    price: row.price || (match?.variant?.price ?? match?.product?.price ?? ''),
    mrp: row.mrp || (match?.variant?.price ?? match?.product?.price ?? ''),
    gst: row.gst || (match?.product?.tax_percentage != null ? String(match.product.tax_percentage) : '18'),
    discount: row.discount || '',
    totalAmt: row.totalAmt || '',
    productId: match?.product?.id || null,
    productVariantId: match?.variant?.id || null,
  };
  return item;
}